// FT-136. Единственное место, где устанавливается факт «сессия мертва».
//
// Повод (скриншот 2026-08-24): на протухшем JWT пользователь видел
// `AxiosError: Request failed with status code 401. Ошибка!`. Дефектов оказалось
// семь, а корень один — этот факт нигде не устанавливался явно: интерсептор,
// мутация стора, setError и компонент догадывались о нём каждый по своему признаку,
// и догадки расходились.
//
// Почему решение живёт здесь, а не в main.js:
//   1. Классифицировать по текущему маршруту физически нельзя. `unsetCurrentUser`
//      синхронно зовёт `router.replace` (store.js:492) внутри 401-обработчика
//      (main.js:394), то есть ДО того, как отвергнутый промис дойдёт до catch
//      компонента: к моменту вызова setError маршрут уже недетерминирован.
//   2. main.js не импортируется ни одним спеком. Всё, что осталось бы там, было бы
//      невидимо и для суйта, и для мутационной проверки.
//   3. Граф импортов уже замкнут (store.js -> router -> store), поэтому модуль не
//      имеет права тянуть стор или роутер и остаётся чистым.
//
// Признаки снимаются на МОМЕНТ ОТПРАВКИ запроса и живут на его config. Считать их в
// момент ответа нельзя: первый же истинный вердикт обнуляет стор и сбрасывает
// `axios.defaults.baseURL` на дефолтный (store.js:450-453, :476), после чего ответы
// 2..N параллельных региональных запросов сравнивались бы уже с другой базой.

// Путь app_version берём из модуля, который его объявляет: страж корпуса
// (tests/unit/antiScrapeRetryLoop.spec.js) запрещает второй литерал — ровно тот
// класс «двух канонов», от которого предостерегает комментарий в main.js:337-341.
import { APP_VERSION_PATH } from "@/utils/antiScrapeRetry";

export const SESSION_SNAPSHOT = "__ftSessionSnapshot";
export const INTENTIONAL_LOGOUT = "__ftIntentionalLogout";
export const SESSION_DEAD = "__ftSessionDead";

// Пути, которые по построению уходят без живой сессии. Перечень выведен из серверных
// `authenticate_user!` с `except`/`only` и запросов компонентов с
// `meta.requiresAuth: false`; сверен с кодом 2026-08-24.
// NB: `tinkoff/add_card` и `remove_card` сюда НЕ входят — они требуют аутентификации
// (tinkoffs_controller.rb:8), хотя и зовутся с публичной страницы клиента.
export const UNAUTHENTICATED_PATHS = [
  "/users",
  "/users/sign_in",
  "/api/v1/auth/request_otp",
  "/api/v1/auth/verify_otp",
  "/api/v1/auth/handoff",
  "/api/v1/signup_verifications",
  "/api/v1/signup_verifications/verify",
  "/api/v1/signup/currencies",
  "/api/v1/restore_password",
  "/api/v1/reset_password",
  "/api/v1/find_booking_for_client_v2",
  "/api/v1/integrations/public_yandex_pays/orders",
  "/api/v1/integrations/public_stripe_payments/orders",
  // FT-193: публичная привязка (POST) и отвязка (DELETE) карты: по номеру бронирования и фамилии; JWT, который глобальный
  // axios может подставить, эндпоинт не читает и 401 из-за него не отдаёт (так же find_booking_for_client_v2).
  "/api/v1/integrations/public/tinkoff/cards",
  // FT-200: бренд проката на странице входа кабинета — анонимно, по подписанному коду компании.
  "/api/v1/client_cabinet/brand",
  APP_VERSION_PATH,
  "/api/v1/passkeys/check",
  "/api/v1/passkeys/authentication_options",
  "/api/v1/passkeys/authenticate",
  // FT-191: PIN-разблокировка общего компьютера идёт по секрету устройства, без токена.
  "/api/v1/trusted_device/unlock",
];

// FT-200 (REQ-04): календарь бронирования `/api/v1/bookings/icalendar_file/:id` — только сотруднику с JWT
// (карточка бронирования); его 401 — истечение сессии. Клиент берёт календарь из ответа входа кабинета.

// Query и hash отрезаем, ведущий слэш приводим. Без этого `/api/v1/reset_password?token=…`
// не совпал бы с записью перечня, а `users/sign_in` (так его зовёт Signin.vue:403) не
// совпал бы с `/users/sign_in`.
export function normalizeRequestPath(url, baseURL) {
  const value = String(url || "").split("#")[0].split("?")[0];
  const absolute = value.match(/^(https?:\/\/[^/?#]+)/i);
  let path = absolute ? value.slice(absolute[1].length) : value;
  if (!path) return "/";
  if (path.charAt(0) !== "/") path = `/${path}`;
  // База может нести путевой префикс (`https://host/api`): у абсолютного URL он войдёт
  // в путь, у относительного — нет, и без отсечения сравнение с перечнем перестало бы
  // совпадать ровно для половины вызовов.
  const baseMatch = String(baseURL || "").match(/^(https?:\/\/[^/?#]+)(\/.*)?$/i);
  if (absolute && baseMatch && absolute[1].toLowerCase() === baseMatch[1].toLowerCase()) {
    const basePath = (baseMatch[2] || "").replace(/\/$/, "");
    if (basePath && path.indexOf(basePath) === 0) {
      path = path.slice(basePath.length) || "/";
    }
  }
  return path;
}

// Запрос по построению уходит без живой сессии? Нужен и классификатору, и решению о
// ретрае (@/utils/retryDecisions): повторять POST users/sign_in с неверным паролем
// нельзя — раньше от этого спасал маршрутный guard, снятый REQ-09.
export function isUnauthenticatedRequest(config) {
  if (!config) return false;
  return isUnauthenticatedPath(normalizeRequestPath(config.url, config.baseURL));
}

// Сравнение РАВЕНСТВОМ, а не вхождением подстроки: иначе запись `users` поглотила бы
// `users/sign_out` (обессмыслив маркер намеренного выхода) и `api/v1/users` — тогда
// настоящее истечение на списке сотрудников было бы объявлено «не сессией».
// Тот же приём и по той же причине применён в antiScrapeRetry.js:34-43.
export function isUnauthenticatedPath(path) {
  return UNAUTHENTICATED_PATHS.indexOf(normalizeRequestPath(path)) !== -1;
}

function hasAuthorization(config) {
  const headers = config.headers || {};
  if (typeof headers.get === "function") {
    const viaGetter = headers.get("Authorization");
    if (viaGetter !== undefined && viaGetter !== null) {
      return viaGetter !== "" && viaGetter !== "null" && viaGetter !== "Bearer null";
    }
  }
  const key = Object.keys(headers).find((name) => name.toLowerCase() === "authorization");
  const value = key ? headers[key] : null;
  return !!value && value !== "null" && value !== "Bearer null";
}

// Снимки на момент отправки (CTR-06). `hasStoredToken` покрывает окно после
// перезагрузки: стор ещё не гидратирован (signedIn: false, store.js:60), а токен из
// localStorage уже подставлен. Без него вердикт был бы ложным, токен остался бы
// нетронутым, а router.beforeEach продолжил бы считать пользователя аутентифицированным
// (router/index.js:759-769) — приложение залипло бы в цикле 401.
export function stampRequest(config, { sessionActive, hasStoredToken, apiOrigin } = {}) {
  if (!config) return config;
  // Идемпотентность обязательна: тот же config уходит повторно — ретраями 401 и 403
  // (`Vue.axios(error.config)` в main.js), и каждый повтор снова проходит через
  // request-интерсептор. Переставить снимок значит выдать запросу прошлой сессии
  // поколение текущей — и тогда его запоздавший 401 снесёт уже НОВУЮ сессию, то есть
  // вернётся ровно тот дефект, ради которого поколение и заведено.
  if (config[SESSION_SNAPSHOT]) return config;
  config[SESSION_SNAPSHOT] = {
    active: !!sessionActive || !!hasStoredToken,
    apiOrigin: !!apiOrigin,
    generation: generation,
  };
  return config;
}

// Маркер намеренного выхода ставится инициатором, а не выводится из URL: так выход
// отличается от истечения по тому, кто его начал.
export function markIntentionalLogout(config) {
  if (config) config[INTENTIONAL_LOGOUT] = true;
  return config;
}

export function isIntentionalLogout(error) {
  return !!(error && error.config && error.config[INTENTIONAL_LOGOUT]);
}

export function isSessionDead(error) {
  if (!error || !error.response || error.response.status !== 401) return false;
  const config = error.config;
  if (!config) return false;
  const snapshot = config[SESSION_SNAPSHOT];
  if (!snapshot || !snapshot.apiOrigin || !snapshot.active) return false;
  // Запрос из прошлой сессии: пользователь уже вошёл заново, и этот 401 относится к
  // мёртвому токену, которого больше нет.
  if (snapshot.generation !== generation) return false;
  if (config[INTENTIONAL_LOGOUT]) return false;
  if (!hasAuthorization(config)) return false;
  return !isUnauthenticatedPath(normalizeRequestPath(config.url, config.baseURL));
}

// --- Дедуп объяснения -----------------------------------------------------------
//
// Объяснение — единственный тост, показанный в момент классификации. Плашку на форме
// входа владелец отменил на приёмке 2026-08-24, увидев её живьём: тост переживает
// переход на форму входа и дублировать его нечем (`DEC-03`).
let announced = false;
// Поколение сессии. Запросы, ушедшие ДО текущего входа, не имеют права разлогинивать:
// при протухании токена 401 прилетает по нескольким параллельным запросам, первый из
// них выкидывает на форму входа, человек успевает войти — и запоздавший ответ старого
// запроса снёс бы уже НОВУЮ сессию. Класс in-flight-запросов, переживающих разлогин,
// описан и в store.js:471-475.
let generation = 0;
// Разлогин выполняется один раз на поколение и НЕ привязан к праву объяснить: иначе
// «уже объяснили» без последующего сброса означало бы «больше не разлогиниваем» —
// молчаливый цикл 401 при живом протухшем токене, инверсия FM-05/CON-05.
let logoutDoneFor = -1;

// Форма входа может быть УЖЕ смонтирована к моменту истечения: в пост-логиновом
// сценарии переход идёт с /signin на /signin и `created`/`mounted` второй раз не
// зовутся. Поэтому нотис не только лежит, но и стучится подписчикам.
// Снекбар физически один (notifications.js:63-67 + App.vue:248-256): второй вызов не
// добавит тост, а затрёт первый и сбросит таймер. Поэтому дедуп стоит на источнике.
export function shouldAnnounce() {
  if (announced) return false;
  announced = true;
  return true;
}

// Зовётся успешным входом — иначе следующее истечение в той же вкладке промолчит.
export function resetSessionExpiry() {
  announced = false;
  generation += 1;
}

// Обработчик истинного вердикта. Делает три вещи разом и в одном месте: объясняет
// (один раз на истечение), оставляет нотис форме входа и помечает объект ошибки,
// чтобы setError промолчал вместо печати `${error}` — второй тост физически затёр бы
// первый (CON-04). Разлогин выполняется ПОСЛЕ пометки и тоста: `unsetCurrentUser`
// синхронно уводит на форму входа (store.js:492), и всё, что не сделано до него,
// приедет уже на другой экран.
//
// Помечаются ВСЕ ошибки одного истечения, а объясняется оно один раз: при протухании
// токена 401 прилетает сразу по нескольким параллельным запросам страницы, и каждый
// дойдёт до своего catch и своего setError.
export function handleSessionDeath(error, { notify, logout } = {}) {
  if (!isSessionDead(error)) return false;

  error[SESSION_DEAD] = true;

  if (shouldAnnounce()) {
    // Объяснение не имеет права уронить разлогин: исключение из notify не должно
    // улететь из интерсептора вместо исходной ошибки.
    try {
      if (notify) notify();
    } catch (e) {
      console.warn("FT-136: не удалось показать объяснение истёкшей сессии", e);
    }
  }

  if (logout && logoutDoneFor !== generation) {
    logoutDoneFor = generation;
    logout();
  }

  return true;
}

// Читает setError: помеченная ошибка уже объяснена источником.
export function isSessionDeadError(error) {
  return !!(error && error[SESSION_DEAD]);
}
